import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';

import { environment } from '../../environments/environment';
import { ApiList } from '../helpers/api';
import { ContentItem, ContentItemType, Page, PageUrlMap, PageType } from '../helpers/page';
import { escapeRegExp, SearchResultPage, SearchResultApp, SearchResultCategory, SearchResults, SearchSnippetPart } from '../helpers/search';
import { NavigationApp, NavigationPage } from '../helpers/navigation';

import { ApiService } from './api.service';

@Injectable({
    providedIn: 'root'
})
export class DocsService {

    private apiMap: PageUrlMap;
    private navigation: Array<NavigationApp>;
    private page: Page;
    private path: Array<string>;
    private version: string;

    private contentChangeSource: Subject<void> = new Subject<void>();
    private contentChange: Observable<void> = this.contentChangeSource.asObservable();

    private pageChangeSource: Subject<void> = new Subject<void>();
    private pageChange: Observable<void> = this.pageChangeSource.asObservable();

    private pageScrollSource: Subject<void> = new Subject<void>();
    private pageScroll: Observable<void> = this.pageScrollSource.asObservable();

    private searchObserver: Subject<SearchResults> = new Subject<SearchResults>();
    private searchObservable: Observable<SearchResults> = this.searchObserver.asObservable();

    private versionChangeSource: Subject<void> = new Subject<void>();
    private versionChange: Observable<void> = this.versionChangeSource.asObservable();

    constructor(
        private apiService: ApiService,
        private router: Router,
    ) { }

    /**
     * Generates human-readable id for heading element.
     * @param heading `HTMLElement` heading object to generate id for.
     */
    private generateHeadingId(heading: HTMLElement): string {
        const text: string = heading.innerText;
        const nospace: string = text.replace(/\s/gi, '-'); // replace spaces with '-' delimiter
        const noduplicate: string = nospace.replace(/-+/gi, '-'); // remove duplicate delimiters
        const alphanumeric: string = noduplicate.replace(/[^\w-]/gi, ''); // remove non-alphanumeric symbols
        const id: string = alphanumeric.slice(0, 32); // limit id length to 32 symbols

        return id;
    }

    /**
     * Breadcrumbs for the active page.
     */
    getBreadcrumbs(): Array<string> {
        const [appSlug, categorySlug, pageSlug]: Array<string> = this.path;
        const breadcrumbs: Array<string> = [];

        for (const app of this.navigation) {
            if (app.slug === appSlug) {
                breadcrumbs.push(app.name);

                for (const category of app.categories) {
                    if (category.slug === categorySlug) {
                        if (category.name) {
                            breadcrumbs.push(category.name);
                        }

                        for (const page of category.pages) {
                            if (page.slug === pageSlug) {
                                breadcrumbs.push(page.name);
                                return breadcrumbs;
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * Content change event observable.
     */
    getContentChange(): Observable<void> {
        return this.contentChange;
    }

    /**
     * Splits content string into `ContentItem` array.
     */
    getContentItems(contentString: string): Array<ContentItem> {
        if (!contentString) {
            return [];
        }

        const items: Array<ContentItem> = [];
        const contentWrapper: HTMLDivElement = document.createElement('div');
        contentWrapper.innerHTML = contentString;

        const childNodes: Array<Node> = Array.from(contentWrapper.childNodes);

        for (const element of childNodes) {
            if (!(element instanceof HTMLElement)) {
                continue;
            }

            if (element instanceof HTMLHeadingElement) {
                element.id = element.id || this.generateHeadingId(element);

                items.push({
                    content: element,
                    type: ContentItemType.Heading,
                });
            } else if (element instanceof HTMLPreElement) {
                const html: string = element.innerHTML;

                if (environment.requestSnippetPrefix.test(html)) {
                    items.push({
                        content: element,
                        type: ContentItemType.RequestSnippet,
                    });
                } else if (environment.codeSnippetPrefix.test(html)) {
                    items.push({
                        content: element,
                        type: ContentItemType.CodeSnippet,
                    });
                } else {
                    items.push({
                        content: element.outerHTML,
                        type: ContentItemType.Pre,
                    });
                }
            } else {
                const textElement: HTMLElement = this.wrapImages(element);

                items.push({
                    content: textElement.outerHTML,
                    type: ContentItemType.Text,
                });
            }
        }

        return items;
    }

    /**
     * Content string of the active page.
     */
    getContentString(): string {
        if (!this.page || !this.page.content) {
            return null;
        }

        let contentString: string;

        if (this.isVersioned()) {
            contentString = this.page.content[this.version];
        } else {
            contentString = <string>this.page.content;
        }

        return contentString;
    }

    /**
     * Default page URL.
     */
    getDefaultRoute(): string {
        for (const app of this.navigation) {
            for (const category of app.categories) {
                for (const page of category.pages) {
                    if (page.is_default) {
                        return `/${app.slug}/${category.slug}/${page.slug}`;
                    }
                }
            }
        }

        return null;
    }

    /**
     * Page routes navigation array.
     */
    getNavigation(): Array<NavigationApp> {
        return this.navigation;
    }

    /**
     * Active page.
     */
    getPage(): Page {
        return this.page;
    }

    /**
     * Active page change event observable.
     */
    getPageChange(): Observable<void> {
        return this.pageChange;
    }

    /**
     * Page scroll event observable.
     */
    getPageScroll(): Observable<void> {
        return this.pageScroll;
    }

    /**
     * Search results event observable.
     */
    getSearch(): Observable<SearchResults> {
        return this.searchObservable;
    }

    /**
     * Selected content version (e.g. production or staging).
     */
    getVersion(): string {
        return this.version;
    }

    /**
     * Content version change event observable.
     */
    getVersionChange(): Observable<void> {
        return this.versionChange;
    }

    /**
     * Available content versions.
     */
    getVersions(): Array<string> {
        if (this.isVersioned()) {
            return Object.keys(this.page.content);
        } else {
            return null;
        }
    }

    /**
     * Checks if the page content is versioned.
     */
    private isVersioned(): boolean {
        return (
            this.page &&
            this.page.page_type === PageType.Versioned &&
            this.page.content &&
            typeof this.page.content === 'object'
        );
    }

    /**
     * Refreshes page content.
     */
    async clearCache(id: string): Promise<void> {
        const url = `${environment.apiUrl}${environment.docsApi}pages/${id}/clear-cache/`;
        const page: Page = await this.apiService.post(url);

        this.page = page;
        this.pageChangeSource.next();
    }

    /**
     * Triggers content change event.
     */
    emitContentChange(): void {
        return this.contentChangeSource.next();
    }


    /**
     * Triggers scroll event.
     */
    emitScroll(): void {
        this.pageScrollSource.next();
    }

    /**
     * Initializes service.
     */
    async init(): Promise<void> {
        const response: ApiList<NavigationApp> = await this.apiService.get(`${environment.apiUrl}${environment.docsApi}applications/`);
        this.navigation = response.results;

        this.apiMap = {};
        for (const app of this.navigation) {
            this.apiMap[app.slug] = {};
            for (const category of app.categories) {
                this.apiMap[app.slug][category.slug] = {};
                for (const page of category.pages) {
                    this.apiMap[app.slug][category.slug][page.slug] = page.url;
                }
            }
        }

        const isRoot: boolean = this.router.url === '/' || this.router.url === '/#/';
        const defaultRoute: string = this.getDefaultRoute();

        if (isRoot && defaultRoute) {
            this.router.navigate([defaultRoute]);
        }
    }

    /**
     * Opens the page for the url slugs.
     */
    async openPage(appSlug: string, categorySlug: string, pageSlug: string): Promise<void> {
        this.path = [appSlug, categorySlug, pageSlug];
        const apiUrl: string = this.apiMap[appSlug][categorySlug][pageSlug];
        if (!apiUrl) {
            this.page = null;
        } else {
            this.page = await this.apiService.get(apiUrl);
        }

        const versions: Array<string> = this.getVersions();
        this.version = versions && versions[0];

        this.pageChangeSource.next();
    }

    /**
     * Splits search snippet string into search snippet parts (to highlight matches).
     * @param snippet Search snippet string.
     * @param query Search query.
     */
    parseSearchSnippet(snippet: string, query: string): Array<SearchSnippetPart> {
        if (!snippet) {
            return [{
                match: false,
                text: '',
            }];
        }

        const matches: Array<string> = snippet.match(new RegExp(`${escapeRegExp(query)}`, 'gi'));

        if (!matches) {
            return [{
                match: false,
                text: snippet,
            }];
        }

        const parts: Array<SearchSnippetPart> = [];
        let currentIndex = 0;

        for (const match of matches) {
            const matchStart: number = snippet.indexOf(match, currentIndex);
            const matchEnd: number = matchStart + match.length;

            parts.push(
                {
                    match: false,
                    text: snippet.slice(currentIndex, matchStart),
                },
                {
                    match: true,
                    text: match,
                }
            );

            currentIndex = matchEnd;
        }

        parts.push({
            match: false,
            text: snippet.slice(currentIndex),
        });

        return parts.filter((part): boolean => {
            return part.text.length > 0;
        });
    }

    /**
     * Searches pages that match the query.
     * @param query Search query.
     */
    async search(query: string): Promise<void> {
        this.searchObserver.next({
            query,
            results: null,
        });

        if (query.length < environment.searchQueryMin) {
            return;
        }

        const url = `${environment.apiUrl}${environment.docsApi}pages/search/?q=${query}&limit=${environment.searchLimit}`;
        const response: ApiList<SearchResultPage> = await this.apiService.get(url);

        const apps: Array<SearchResultApp> = [];

        for (const result of response.results) {
            let appIndex: number = apps.findIndex((resultApp: SearchResultApp): boolean => {
                return resultApp.application_slug === result.application_slug;
            });

            if (appIndex < 0) {
                appIndex = apps.length;

                apps.push({
                    application: result.application,
                    application_slug: result.application_slug,
                    categories: [],
                });
            }

            const app: SearchResultApp = apps[appIndex];

            let categoryIndex: number = app.categories.findIndex((resultCategory: SearchResultCategory): boolean => {
                return resultCategory.category_slug === result.category_slug;
            });

            if (categoryIndex < 0) {
                categoryIndex = app.categories.length;

                app.categories.push({
                    category: result.category,
                    category_slug: result.category_slug,
                    pages: [],
                });
            }

            const category: SearchResultCategory = app.categories[categoryIndex];

            category.pages.push(result);
        }

        this.searchObserver.next({
            query,
            results: apps,
        });
    }

    /**
     * Changes the active convent version.
     * @param version New content version.
     */
    setVersion(version: string): void {
        this.version = version;
        this.versionChangeSource.next();
    }


    /**
     * Changes page visibility (public or private).
     * @param id Page ID.
     * @param value Visibility value.
     */
    private async setVisibility(id: string, value: boolean): Promise<void> {
        const url = `${environment.apiUrl}${environment.docsApi}/pages/${id}/set-visibility/`;

        const result: NavigationPage = await this.apiService.post(url, {
            value,
        });

        if (id === this.page.id) {
            this.page.is_public = result.is_public;
        }

        for (const app of this.navigation) {
            for (const category of app.categories) {
                for (const page of category.pages) {
                    if (page.id === id) {
                        page.is_public = result.is_public;
                    }
                }
            }
        }
    }

    /**
     * Toggles page visibility (public or private).
     * @param id Page ID.
     * @param visibility Current visibility value.
     */
    async toggleVisibility(id: string, visibility: boolean): Promise<boolean> {
        const value: boolean = !visibility;
        const message: string = environment.visibilityMessage.get(value);

        if (confirm(message)) {
            await this.setVisibility(id, value);
            return value;
        } else {
            return visibility;
        }
    }

    /**
     * Wraps images with anchor tag to support LightGallery.
     * @param source Content item element.
     */
    private wrapImages(source: HTMLElement): HTMLElement {
        const target: HTMLElement = <HTMLElement>source.cloneNode(true);

        if (target.tagName === 'IMG') {
            const image: HTMLImageElement = <HTMLImageElement>target;

            const wrapper: HTMLAnchorElement = document.createElement('a');
            wrapper.href = image.src;
            wrapper.classList.add(environment.lightGalleryWrapper);
            wrapper.appendChild(image);

            return wrapper;
        } else {
            const children: Array<Node> = Array.from(target.childNodes);

            for (const child of children) {
                if (child instanceof HTMLElement) {
                    target.replaceChild(this.wrapImages(<HTMLElement>child), child);
                }
            }

            return target;
        }
    }
}
